import { useState, useMemo, useCallback, useEffect, useRef } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { GapFilterBanner } from '@/components/repGaps/GapFilterBanner'
import { repGapFor } from '@/lib/repGaps'
import {
  useLeads,
  useDeleteLead,
  useBulkDeleteLeads,
  useBulkUpdateLeads,
  useBulkAssignLeads,
  useUsers,
} from '@/hooks/use-api'
import { ownerPickerOptions } from '@/lib/salesReps'
import { usePagination } from '@/hooks/use-pagination'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { RefreshButton } from '@/components/RefreshButton'
import { SearchWithRecent } from '@/components/ui/search-with-recent'
import { ResizableTable } from '@/components/ui/resizable-table'
import { CategoryBadge } from '@/components/ui/CategoryBadge'
import { UserPlus, Plus, TrendingUp, Users2, Star, Download } from 'lucide-react'
import type { Lead } from '@/types'
import { exportLeadsToCSV } from '@/lib/csv-export'
import { CreateLeadModal } from '@/components/CreateLeadModal'
import { ConvertLeadModal } from '@/components/ConvertLeadModal'
import { BulkActionsBar } from '@/components/BulkActionsBar'
import { BulkDeleteDialog } from '@/components/BulkDeleteDialog'
import { BulkUpdateModal, type BulkUpdateField } from '@/components/BulkUpdateModal'
import { BulkAssignModal } from '@/components/BulkAssignModal'
import { MergeRecordsModal } from '@/components/MergeRecordsModal'
import { createLeadsColumns, leadsDefaultColumnSizing } from '@/config/columns/leads-columns'
import { ColumnPicker } from '@/components/ui/column-picker'
import { useColumnVisibility } from '@/hooks/use-column-visibility'
import { SearchableMultiSelect } from '@/components/ui/searchable-multi-select'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { usePicklistOptions } from '@/hooks/use-picklists'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { useToast } from '@/hooks/use-toast'
import { useReadOnly } from '@/hooks/use-read-only'
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { useUser } from '@/contexts/UserContext'
import { usePersistentState, makeFilterKey } from '@/hooks/use-persistent-state'
import { MobileLeadList } from '@/components/MobileLeadCard'
import { useMobile } from '@/hooks/use-mobile'
import { explainError } from '@/lib/explain-error'

export default function Leads() {
  const { currentUser, isAdmin, impersonatedUser } = useUser()
  const fk = makeFilterKey(currentUser?.id, 'leads')
  const navigate = useNavigate()
  const [search, setSearch] = useState('')
  const { page, firstPage, prevPage, nextPage, extract } = usePagination()
  const [showMyLeads, setShowMyLeads] = usePersistentState(fk('showMy'), !isAdmin)
  const [sortBy, setSortBy] = usePersistentState(fk('sortBy'), 'created_date')
  const [sortOrder, setSortOrder] = usePersistentState<'asc' | 'desc'>(fk('sortOrder'), 'desc')
  // Marketing Status multiselect filter (empty = no filter).
  const [marketingStatuses, setMarketingStatuses] = useState<string[]>([])
  const marketingStatusOptions = usePicklistOptions('lead', 'marketing_status__c')
  // Column-level filters (empty = no filter).
  const [statuses, setStatuses] = useState<string[]>([])
  const statusOptions = usePicklistOptions('lead', 'status')
  const [leadSources, setLeadSources] = useState<string[]>([])
  const leadSourceOptions = usePicklistOptions('lead', 'lead_source')
  const [ownerIds, setOwnerIds] = useState<string[]>([])
  const [dateFrom, setDateFrom] = useState('')
  const [dateTo, setDateTo] = useState('')

  // Owner scope from a dashboard deep-link (?owner_id=… / ?my). Overrides the
  // My/All toggle on arrival so the list matches the card; cleared on toggle.
  const [searchParams] = useSearchParams()
  const [deepLinkOwnerId, setDeepLinkOwnerId] = useState<string | undefined>(undefined)
  // Exec Sales Dashboard "Follow-through" drill-down (?gap=untouched_leads).
  const repGap = repGapFor('lead', searchParams.get('gap'))
  const dashboardAppliedRef = useRef(false)
  useEffect(() => {
    if (dashboardAppliedRef.current) return
    if (!searchParams.has('owner_id') && !searchParams.has('my') && !searchParams.has('gap')) return
    dashboardAppliedRef.current = true
    const me = impersonatedUser?.id || currentUser?.id
    const ownerId = searchParams.get('owner_id') || (searchParams.has('my') ? me : undefined)
    setDeepLinkOwnerId(ownerId)
    setShowMyLeads(!!ownerId && ownerId === me)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])
  const [createModalOpen, setCreateModalOpen] = useState(false)
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
  const [leadToDelete, setLeadToDelete] = useState<{ id: string; name: string } | null>(null)
  const [convertModalOpen, setConvertModalOpen] = useState(false)
  const [leadToConvert, setLeadToConvert] = useState<Lead | null>(null)
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set())
  const [bulkDeleteDialogOpen, setBulkDeleteDialogOpen] = useState(false)
  const [bulkUpdateModalOpen, setBulkUpdateModalOpen] = useState(false)
  const [bulkAssignModalOpen, setBulkAssignModalOpen] = useState(false)
  const [mergeModalOpen, setMergeModalOpen] = useState(false)
  const isMobile = useMobile()

  // Only Admin/Super Admin can export or change owner (and not when impersonating a non-admin)
  const canExport =
    isAdmin && (!impersonatedUser || ['Admin', 'Super Admin'].includes(impersonatedUser.user_type))
  const canChangeOwner =
    isAdmin && (!impersonatedUser || ['Admin', 'Super Admin'].includes(impersonatedUser.user_type))
  // Only Admin/Super Admin can delete (single or bulk) — for cleanup
  const canDelete =
    isAdmin && (!impersonatedUser || ['Admin', 'Super Admin'].includes(impersonatedUser.user_type))
  const { data, isLoading } = useLeads({
    search,
    page,
    owner_id:
      deepLinkOwnerId ?? (showMyLeads ? impersonatedUser?.id || currentUser?.id : undefined),
    user_email: showMyLeads ? impersonatedUser?.email || currentUser?.email : undefined,
    marketing_status__c: marketingStatuses.length ? marketingStatuses.join(',') : undefined,
    status: statuses.length ? statuses.join(',') : undefined,
    lead_source: leadSources.length ? leadSources.join(',') : undefined,
    owner_ids: ownerIds.length ? ownerIds.join(',') : undefined,
    date_from: dateFrom || undefined,
    date_to: dateTo || undefined,
    gap: repGap,
    sort_by: sortBy,
    sort_order: sortOrder,
  })

  const handleSort = useCallback(
    (field: string) => {
      if (sortBy === field) {
        setSortOrder((prev) => (prev === 'asc' ? 'desc' : 'asc'))
      } else {
        setSortBy(field)
        setSortOrder('asc')
      }
      firstPage()
    },
    [sortBy, firstPage]
  )
  const deleteMutation = useDeleteLead()
  const bulkDeleteMutation = useBulkDeleteLeads()
  const bulkUpdateMutation = useBulkUpdateLeads()
  const { data: usersData } = useUsers({ limit: 10000 })
  const users = usersData?.data ?? []
  const bulkAssignMutation = useBulkAssignLeads()
  const { toast } = useToast()
  const isReadOnly = useReadOnly()

  const {
    items: leads,
    total,
    currentPage,
    limit,
    totalPages,
    hasNextPage,
    hasPrevPage,
    stats,
  } = extract(data)

  // Handle delete confirmation
  const handleDeleteConfirm = async () => {
    if (!leadToDelete) return

    try {
      await deleteMutation.mutateAsync(leadToDelete.id)

      toast({
        title: 'Success',
        description: `Lead "${leadToDelete.name}" has been deleted.`,
      })

      setDeleteDialogOpen(false)
      setLeadToDelete(null)
    } catch (error) {
      const errorMessage = explainError(error, 'Failed to delete lead')
      toast({
        title: 'Error',
        description: errorMessage,
        variant: 'destructive',
      })
    }
  }

  // Handle selection
  const handleToggleSelect = (id: string) => {
    setSelectedIds((prev) => {
      const newSet = new Set(prev)
      if (newSet.has(id)) {
        newSet.delete(id)
      } else {
        newSet.add(id)
      }
      return newSet
    })
  }

  const handleToggleSelectAll = () => {
    if (selectedIds.size === leads.length) {
      setSelectedIds(new Set())
    } else {
      setSelectedIds(new Set(leads.map((lead) => lead.id)))
    }
  }

  const handleClearSelection = () => {
    setSelectedIds(new Set())
  }

  // Handle bulk delete
  const handleBulkDelete = async () => {
    try {
      const idsArray = Array.from(selectedIds)
      await bulkDeleteMutation.mutateAsync(idsArray)

      toast({
        title: 'Success',
        description: `${idsArray.length} lead${idsArray.length === 1 ? '' : 's'} deleted successfully.`,
      })

      setBulkDeleteDialogOpen(false)
      setSelectedIds(new Set())
    } catch (error) {
      const errorMessage = explainError(error, 'Failed to delete leads')
      toast({
        title: 'Error',
        description: errorMessage,
        variant: 'destructive',
      })
    }
  }

  // Handle bulk update
  const handleBulkUpdate = async (updates: Record<string, any>) => {
    try {
      await bulkUpdateMutation.mutateAsync({ ids: Array.from(selectedIds), updates })

      toast({
        title: 'Success',
        description: `${selectedIds.size} lead${selectedIds.size === 1 ? '' : 's'} updated successfully.`,
      })

      setBulkUpdateModalOpen(false)
      setSelectedIds(new Set())
    } catch (error) {
      const errorMessage = explainError(error, 'Failed to update leads')
      toast({
        title: 'Error',
        description: errorMessage,
        variant: 'destructive',
      })
    }
  }

  // Handle bulk assign
  const handleBulkAssign = async (ownerId: string, notifyOwner: boolean) => {
    try {
      await bulkAssignMutation.mutateAsync({
        ids: Array.from(selectedIds),
        owner_id: ownerId,
        notify_owner: notifyOwner,
      })

      toast({
        title: 'Success',
        description: `${selectedIds.size} lead${selectedIds.size === 1 ? '' : 's'} assigned successfully.`,
      })

      setBulkAssignModalOpen(false)
      setSelectedIds(new Set())
    } catch (error) {
      const errorMessage = explainError(error, 'Failed to assign leads')
      toast({
        title: 'Error',
        description: errorMessage,
        variant: 'destructive',
      })
    }
  }

  // Handle CSV export
  const handleExportCSV = () => {
    // For now, export the current page of leads
    // In a production app, you might want to fetch all leads from the API
    exportLeadsToCSV(leads)
    toast({
      title: 'Export Successful',
      description: `Exported ${leads.length} leads to CSV`,
    })
  }

  // Define field configurations
  const leadsUpdateFields: BulkUpdateField[] = [
    {
      key: 'status',
      label: 'Status',
      type: 'select',
      options: [
        { value: 'Open', label: 'Open' },
        { value: 'Qualified', label: 'Qualified' },
        { value: 'Unqualified', label: 'Unqualified' },
      ],
    },
    {
      key: 'rating',
      label: 'Rating',
      type: 'select',
      options: [
        { value: 'Hot', label: 'Hot' },
        { value: 'Warm', label: 'Warm' },
        { value: 'Cold', label: 'Cold' },
      ],
    },
    { key: 'lead_source', label: 'Lead Source', type: 'text', placeholder: 'Enter lead source' },
    ...(canChangeOwner
      ? [
          {
            key: 'owner_id',
            label: 'Owner',
            type: 'select' as const,
            options: ownerPickerOptions(users).map((u) => ({ value: u.id, label: u.name })),
          },
        ]
      : []),
  ]

  // Create columns with callbacks
  const columns = useMemo(
    () =>
      createLeadsColumns({
        onDeleteLead: canDelete
          ? (leadId: string, name: string) => {
              setLeadToDelete({ id: leadId, name })
              setDeleteDialogOpen(true)
            }
          : undefined,
        onConvertLead: (lead: Lead) => {
          setLeadToConvert(lead)
          setConvertModalOpen(true)
        },
        selectedIds,
        onToggleSelect: handleToggleSelect,
        onToggleSelectAll: handleToggleSelectAll,
        allSelected: selectedIds.size === leads.length && leads.length > 0,
      }),
    [selectedIds, leads.length, canDelete]
  )

  const [columnVisibility, setColumnVisibility] = useColumnVisibility('leads-column-visibility', {
    marketing_status__c: false,
  })

  return (
    <div className="space-y-4">
      {selectedIds.size > 0 && (
        <BulkActionsBar
          selectedCount={selectedIds.size}
          entityType="lead"
          onClearSelection={handleClearSelection}
          onBulkDelete={canDelete ? () => setBulkDeleteDialogOpen(true) : undefined}
          onBulkUpdate={() => setBulkUpdateModalOpen(true)}
          onBulkAssign={canChangeOwner ? () => setBulkAssignModalOpen(true) : undefined}
          onMerge={() => setMergeModalOpen(true)}
          isDeleting={bulkDeleteMutation.isPending}
          isUpdating={bulkUpdateMutation.isPending}
          isAssigning={bulkAssignMutation.isPending}
        />
      )}

      <div className="flex items-center justify-between">
        <div>
          <div className="flex items-center gap-3">
            <CategoryBadge type="lead" />
            <h1 className="text-3xl font-bold tracking-tight">Leads</h1>
          </div>
          <p className="text-muted-foreground mt-1">Manage your sales leads and pipeline</p>
          <GapFilterBanner
            gap={repGap}
            clearTo="/leads"
            ownerName={
              deepLinkOwnerId
                ? (users.find((u) => u.id === deepLinkOwnerId)?.name ?? 'selected rep')
                : undefined
            }
          />
        </div>
        <div className="flex gap-2">
          <RefreshButton queryKey={['leads']} label="leads" />
          {canExport && (
            <Button variant="outline" aria-label="Export leads to CSV" onClick={handleExportCSV}>
              <Download className="mr-2 h-4 w-4" aria-hidden="true" />
              Export
            </Button>
          )}
          <Button
            aria-label="Create new lead"
            onClick={() => setCreateModalOpen(true)}
            disabled={isReadOnly}
            title={isReadOnly ? 'Cannot create leads while viewing as another user' : undefined}
          >
            <Plus className="mr-2 h-4 w-4" aria-hidden="true" />
            New Lead
          </Button>
        </div>
      </div>

      <div className="grid gap-2 md:grid-cols-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-0">
            <CardTitle className="text-sm font-medium">Total Leads</CardTitle>
            <UserPlus className="h-3.5 w-3.5 text-muted-foreground" aria-hidden="true" />
          </CardHeader>
          <CardContent className="p-3">
            <div className="text-xl font-bold leading-none">{total}</div>
            <p className="text-xs text-muted-foreground leading-tight">
              Showing {leads.length} on this page
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-0">
            <CardTitle className="text-sm font-medium">New Leads</CardTitle>
            <Star className="h-3.5 w-3.5 text-muted-foreground" aria-hidden="true" />
          </CardHeader>
          <CardContent className="p-3">
            <div className="text-xl font-bold leading-none">{stats?.new ?? 0}</div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-0">
            <CardTitle className="text-sm font-medium">Qualified</CardTitle>
            <TrendingUp className="h-3.5 w-3.5 text-muted-foreground" aria-hidden="true" />
          </CardHeader>
          <CardContent className="p-3">
            <div className="text-xl font-bold leading-none">{stats?.qualified ?? 0}</div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-0">
            <CardTitle className="text-sm font-medium">Converted</CardTitle>
            <Users2 className="h-3.5 w-3.5 text-muted-foreground" aria-hidden="true" />
          </CardHeader>
          <CardContent className="p-3">
            <div className="text-xl font-bold leading-none">{stats?.converted ?? 0}</div>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>{showMyLeads ? 'My Leads' : 'All Leads'}</CardTitle>
          <CardDescription>View and manage your sales leads</CardDescription>
          <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center">
            <Tabs
              defaultValue="all"
              value={showMyLeads ? 'my' : 'all'}
              onValueChange={(value) => {
                setDeepLinkOwnerId(undefined)
                setShowMyLeads(value === 'my')
                firstPage()
              }}
            >
              <TabsList>
                <TabsTrigger value="all" aria-label="Show all leads">
                  All Leads
                </TabsTrigger>
                <TabsTrigger value="my" aria-label="Show only my leads">
                  My Leads
                </TabsTrigger>
              </TabsList>
            </Tabs>
            <SearchableMultiSelect
              label="Marketing Status"
              options={marketingStatusOptions.map((v) => ({ value: v, label: v }))}
              selected={marketingStatuses}
              onChange={(next) => {
                setMarketingStatuses(next)
                firstPage()
              }}
              searchPlaceholder="Filter marketing status..."
            />
            <SearchableMultiSelect
              label="Status"
              options={statusOptions.map((v) => ({ value: v, label: v }))}
              selected={statuses}
              onChange={(next) => {
                setStatuses(next)
                firstPage()
              }}
              searchPlaceholder="Filter status..."
            />
            <SearchableMultiSelect
              label="Lead Source"
              options={leadSourceOptions.map((v) => ({ value: v, label: v }))}
              selected={leadSources}
              onChange={(next) => {
                setLeadSources(next)
                firstPage()
              }}
              searchPlaceholder="Filter lead source..."
            />
            <SearchableMultiSelect
              label="Owner"
              options={ownerPickerOptions(users).map((u) => ({ value: u.id, label: u.name }))}
              selected={ownerIds}
              onChange={(next) => {
                setOwnerIds(next)
                firstPage()
              }}
              searchPlaceholder="Filter owner..."
            />
            <div className="flex items-end gap-2">
              <div className="flex flex-col gap-1">
                <Label htmlFor="lead-date-from" className="text-xs text-muted-foreground">
                  Added from
                </Label>
                <Input
                  id="lead-date-from"
                  type="date"
                  value={dateFrom}
                  max={dateTo || undefined}
                  onChange={(e) => {
                    setDateFrom(e.target.value)
                    firstPage()
                  }}
                  className="h-9 w-[9.5rem]"
                />
              </div>
              <div className="flex flex-col gap-1">
                <Label htmlFor="lead-date-to" className="text-xs text-muted-foreground">
                  Added to
                </Label>
                <Input
                  id="lead-date-to"
                  type="date"
                  value={dateTo}
                  min={dateFrom || undefined}
                  onChange={(e) => {
                    setDateTo(e.target.value)
                    firstPage()
                  }}
                  className="h-9 w-[9.5rem]"
                />
              </div>
            </div>
            <SearchWithRecent
              value={search}
              onChange={setSearch}
              placeholder="Search leads..."
              ariaLabel="Search leads"
              entityKey="leads"
            />
            <ColumnPicker
              columns={columns}
              columnVisibility={columnVisibility}
              onColumnVisibilityChange={setColumnVisibility}
            />
          </div>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="text-center py-8 text-muted-foreground">Loading leads...</div>
          ) : leads.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              No leads found. Create your first lead to get started.
            </div>
          ) : isMobile ? (
            <MobileLeadList
              leads={leads}
              selectedIds={selectedIds}
              onToggleSelect={handleToggleSelect}
              onDelete={(lead) => {
                setLeadToDelete({ id: lead.id, name: lead.name ?? '' })
                setDeleteDialogOpen(true)
              }}
            />
          ) : (
            <ResizableTable
              columns={columns}
              data={leads}
              storageKey="leads-column-widths"
              defaultColumnSizing={leadsDefaultColumnSizing}
              sortBy={sortBy}
              sortOrder={sortOrder}
              onSort={handleSort}
              columnVisibility={columnVisibility}
              onColumnVisibilityChange={setColumnVisibility}
            />
          )}

          {/* Pagination */}
          {!isLoading && total > limit && (
            <div className="flex items-center justify-between px-2 py-4">
              <div className="text-sm text-muted-foreground">
                Showing {(currentPage - 1) * limit + 1} to {Math.min(currentPage * limit, total)} of{' '}
                {total} leads
              </div>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={prevPage} disabled={!hasPrevPage}>
                  Previous
                </Button>
                <div className="flex items-center gap-1 text-sm">
                  Page {currentPage} of {totalPages}
                </div>
                <Button variant="outline" size="sm" onClick={nextPage} disabled={!hasNextPage}>
                  Next
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      <CreateLeadModal
        open={createModalOpen}
        onOpenChange={setCreateModalOpen}
        onCreated={(lead) => navigate(`/leads/${lead.id}`)}
      />

      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent aria-describedby="delete-lead-description">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Lead</AlertDialogTitle>
            <AlertDialogDescription id="delete-lead-description">
              Are you sure you want to delete the lead "{leadToDelete?.name}"? This action cannot be
              undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel aria-label="Cancel delete operation">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteConfirm}
              disabled={deleteMutation.isPending}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              aria-label={`Confirm delete lead ${leadToDelete?.name}`}
            >
              {deleteMutation.isPending ? 'Deleting...' : 'Delete'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <ConvertLeadModal
        lead={leadToConvert}
        open={convertModalOpen}
        onOpenChange={setConvertModalOpen}
      />

      <BulkDeleteDialog
        open={bulkDeleteDialogOpen}
        onOpenChange={setBulkDeleteDialogOpen}
        selectedCount={selectedIds.size}
        entityType="lead"
        onConfirm={handleBulkDelete}
        isDeleting={bulkDeleteMutation.isPending}
      />

      <BulkUpdateModal
        open={bulkUpdateModalOpen}
        onOpenChange={setBulkUpdateModalOpen}
        selectedCount={selectedIds.size}
        entityType="lead"
        fields={leadsUpdateFields}
        onConfirm={handleBulkUpdate}
        isUpdating={bulkUpdateMutation.isPending}
      />

      <BulkAssignModal
        open={bulkAssignModalOpen}
        onOpenChange={setBulkAssignModalOpen}
        selectedCount={selectedIds.size}
        entityType="lead"
        onConfirm={handleBulkAssign}
        isAssigning={bulkAssignMutation.isPending}
      />

      {selectedIds.size === 2 &&
        (() => {
          const [idA, idB] = Array.from(selectedIds) as [string, string]
          return (
            <MergeRecordsModal
              entityType="Lead"
              recordIdA={idA}
              recordIdB={idB}
              open={mergeModalOpen}
              onOpenChange={(open) => {
                setMergeModalOpen(open)
                if (!open) setSelectedIds(new Set())
              }}
            />
          )
        })()}
    </div>
  )
}
